import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The application calendar. Every aggregate the admin app filters over is bucketed by the backend
 * in this zone (`APPLICATION_TIME_ZONE` / `startOfDayInEasternTime` in `greenway_api`), so a filter
 * edge means a day on *this* calendar — not the viewer's (ADR-0007, amended for ADO 120504).
 */
export const APPLICATION_TIME_ZONE = 'America/New_York';

/** Short marker appended to a date-range filter's label, so the admin knows whose calendar is meant. */
export const APPLICATION_TIME_ZONE_LABEL = 'ET';

/**
 * Today on the application calendar, expressed as a **browser-zone** dayjs at midnight.
 *
 * The zone matters: antd's picker hands `disabledDate` cells in the browser's zone, so the bound it
 * is compared against has to live in that zone too or `isAfter(…, 'day')` compares two different
 * day boundaries. Only the calendar date is taken from Eastern; the offset stays local, which makes
 * every comparison below a plain calendar-day comparison.
 */
export function todayInAppTz(): Dayjs {
  return dayjs(dayjs().tz(APPLICATION_TIME_ZONE).format('YYYY-MM-DD'), 'YYYY-MM-DD');
}

/** Is this picker cell a date the application calendar has not reached yet? */
export function isAfterAppToday(value: Dayjs): boolean {
  return value.isAfter(todayInAppTz(), 'day');
}

/** Is this picker cell a date the application calendar has already finished? */
export function isBeforeAppToday(value: Dayjs): boolean {
  return value.isBefore(todayInAppTz(), 'day');
}

/**
 * Mark a date-range filter's label with the application calendar (BA ruling on ADO 120504, option
 * A). Applied to the visible legend **and** the picker's accessible name so the two stay one
 * string. Idempotent, so a label that already carries the marker cannot collect a second.
 */
export function appTzLabel(label: string): string {
  const marker = ` (${APPLICATION_TIME_ZONE_LABEL})`;
  return label.endsWith(marker) ? label : `${label}${marker}`;
}

/**
 * A UTC instant rendered on the application clock (ADO 120986). Every date-range filter asks for an
 * Eastern day (above), so a row it returns has to read as that day for every admin wherever they sit
 * — rendering the instant in the browser's zone showed a 22 Sep ET order as 23 Sep to an admin in
 * IST. Use this for **instants** only (order, payment, request, log times); a date-only wire value
 * (`YYYY-MM-DD`: delivery, warranty-valid-till, subscription dates) is already on the application
 * calendar and is formatted without conversion (ADR-0007).
 */
export function formatInAppTz(iso: string, pattern: string): string {
  return dayjs(iso).tz(APPLICATION_TIME_ZONE).format(pattern);
}

/**
 * The Eastern calendar day of an instant, as a **browser-zone** dayjs at midnight — the same shape
 * as `todayInAppTz`, for the same reason: antd hands a picker's `disabledDate` its cells in the
 * browser's zone, so a bound derived from an instant must carry the Eastern date in that zone or
 * `isBefore(…, 'day')` compares two different day boundaries (ADO 120986).
 */
export function appDayOf(iso: string): Dayjs {
  return dayjs(formatInAppTz(iso, 'YYYY-MM-DD'), 'YYYY-MM-DD');
}
